/**
 * Serum Concentration & Pharmacokinetics Utility
 * Models two-compartment / Bateman absorption and elimination kinetics
 * for GLP-1 receptor agonists and amylin mimetics.
 * Reconstructs protocol history from audit logs, predecessor cycles, and active schedules.
 */

import { isProtocolScheduledOnDate, formatDateIso, parseDateIso } from './calendarUtils.js';
import { calculateEffectiveStatus } from './statusUtils.js';

/**
 * Supported compounds configuration
 * Includes scientific half-lives, time to peak (Tmax), colors, mechanisms, and alias dictionaries.
 */
export const TRACKED_COMPOUNDS = {
  semaglutide: {
    id: 'semaglutide',
    name: 'Semaglutide',
    mechanism: 'GLP-1 Receptor Agonist',
    halfLifeDays: 7.0, // ~168 hours
    tMaxDays: 1.25, // ~30 hours
    color: '#06B6D4', // cyan-500
    colorMuted: '#06B6D433',
    textColor: 'text-cyan-400',
    borderColor: 'border-cyan-500/40',
    bgColor: 'bg-cyan-500/10',
    fillGradient: ['rgba(6, 182, 212, 0.45)', 'rgba(6, 182, 212, 0.02)'],
    commonDosingSchedule: 'Weekly',
    typicalDoseRange: '0.25 mg – 2.4 mg',
    brandNames: ['Ozempic', 'Wegovy', 'Rybelsus'],
    aliases: [
      'semaglutide',
      'sema',
      'wegovy',
      'ozempic',
      'rybelsus',
      'semaglutid'
    ]
  },
  tirzepatide: {
    id: 'tirzepatide',
    name: 'Tirzepatide',
    mechanism: 'Dual GIP / GLP-1 Receptor Agonist',
    halfLifeDays: 5.0, // ~120 hours
    tMaxDays: 1.0, // ~24 hours
    color: '#10B981', // emerald-500
    colorMuted: '#10B98133',
    textColor: 'text-emerald-400',
    borderColor: 'border-emerald-500/40',
    bgColor: 'bg-emerald-500/10',
    fillGradient: ['rgba(16, 185, 129, 0.45)', 'rgba(16, 185, 129, 0.02)'],
    commonDosingSchedule: 'Weekly',
    typicalDoseRange: '2.5 mg – 15 mg',
    brandNames: ['Mounjaro', 'Zepbound'],
    aliases: [
      'tirzepatide',
      'tirz',
      'tirzep',
      'zep',
      'zepbound',
      'mounjaro',
      'tirzepatid'
    ]
  },
  retatrutide: {
    id: 'retatrutide',
    name: 'Retatrutide',
    mechanism: 'Triple GIP / GLP-1 / Glucagon Agonist',
    halfLifeDays: 6.0, // ~144 hours
    tMaxDays: 1.2, // ~28 hours
    color: '#F59E0B', // amber-500
    colorMuted: '#F59E0B33',
    textColor: 'text-amber-400',
    borderColor: 'border-amber-500/40',
    bgColor: 'bg-amber-500/10',
    fillGradient: ['rgba(245, 158, 11, 0.45)', 'rgba(245, 158, 11, 0.02)'],
    commonDosingSchedule: 'Weekly',
    typicalDoseRange: '1 mg – 12 mg',
    brandNames: ['LY3437943', 'Triple G'],
    aliases: [
      'retatrutide',
      'reta',
      'ret',
      'ly3437943',
      'triple g',
      'ggg',
      'retatrutid'
    ]
  },
  cagrilintide: {
    id: 'cagrilintide',
    name: 'Cagrilintide',
    mechanism: 'Long-Acting Amylin Analogue',
    halfLifeDays: 7.5, // ~180 hours (159–195h)
    tMaxDays: 1.5, // ~36 hours
    color: '#EC4899', // pink-500
    colorMuted: '#EC489933',
    textColor: 'text-pink-400',
    borderColor: 'border-pink-500/40',
    bgColor: 'bg-pink-500/10',
    fillGradient: ['rgba(236, 72, 153, 0.45)', 'rgba(236, 72, 153, 0.02)'],
    commonDosingSchedule: 'Weekly',
    typicalDoseRange: '0.16 mg – 2.4 mg (160–2400 mcg)',
    brandNames: ['CagriSema component', 'NN9838'],
    aliases: [
      'cagrilintide',
      'cag',
      'cagri',
      'cagrisema',
      'nn9838',
      'cagrilintid'
    ]
  },
  eloralintide: {
    id: 'eloralintide',
    name: 'Eloralintide',
    mechanism: 'Selective Amylin Receptor Agonist',
    halfLifeDays: 14.0, // ~336 hours (13–15 days)
    tMaxDays: 2.0, // ~48 hours
    color: '#8B5CF6', // violet-500
    colorMuted: '#8B5CF633',
    textColor: 'text-violet-400',
    borderColor: 'border-violet-500/40',
    bgColor: 'bg-violet-500/10',
    fillGradient: ['rgba(139, 92, 246, 0.45)', 'rgba(139, 92, 246, 0.02)'],
    commonDosingSchedule: 'Weekly',
    typicalDoseRange: '0.5 mg – 9 mg',
    brandNames: ['LY3841136'],
    aliases: [
      'eloralintide',
      'elora',
      'elo',
      'ly3841136',
      'eloralintid'
    ]
  }
};

/**
 * Smart compound identification.
 * Inspects protocol name, notes, and intendedOutcome against known aliases and word stems.
 * Returns compound definition or null if not a tracked GLP-1 / amylin agonist.
 */
export function identifyCompound(protocol) {
  if (!protocol) return null;

  const rawName = (protocol.name || '').toLowerCase();
  const rawNotes = (protocol.notes || '').toLowerCase();
  const rawOutcome = (protocol.intendedOutcome || '').toLowerCase();
  const combinedText = `${rawName} ${rawNotes} ${rawOutcome}`;

  // Break text into alphanumeric tokens for exact word matching
  const tokens = combinedText.match(/[a-z0-9]+/g) || [];

  for (const compound of Object.values(TRACKED_COMPOUNDS)) {
    for (const alias of compound.aliases) {
      const aliasLower = alias.toLowerCase();

      // If alias is long enough, check substring or token
      if (aliasLower.length >= 4) {
        if (rawName.includes(aliasLower) || tokens.includes(aliasLower)) {
          return compound;
        }
      } else {
        // Short aliases (e.g. 'cag', 'ret', 'elo', 'zep') require word-boundary matching
        if (tokens.includes(aliasLower)) {
          return compound;
        }
      }
    }
  }

  // Secondary regex boundary check across compound keys
  for (const compound of Object.values(TRACKED_COMPOUNDS)) {
    for (const alias of compound.aliases) {
      const pattern = new RegExp(`\\b${alias}\\b`, 'i');
      if (pattern.test(protocol.name) || pattern.test(protocol.intendedOutcome)) {
        return compound;
      }
    }
  }

  return null;
}

/**
 * Normalize raw dosage value and unit to milligrams (mg).
 */
export function normalizeDoseValueToMg(rawDose, rawUnit = 'mg') {
  const num = Number(rawDose) || 0;
  if (num <= 0) return 0;
  const unit = (rawUnit || 'mg').toLowerCase().trim();

  if (unit.includes('mcg') || unit.includes('ug') || unit.includes('microgram')) {
    return num / 1000;
  }
  if (unit === 'g' || unit === 'gram' || unit === 'grams') {
    return num * 1000;
  }
  return num;
}

/**
 * Normalize protocol dose to milligrams (mg).
 */
export function normalizeDoseToMg(protocol) {
  if (!protocol) return 0;
  return normalizeDoseValueToMg(protocol.dosageMin, protocol.unit);
}

/**
 * Calculate rate constants for Bateman function:
 * ke = ln(2) / tHalf
 * ka estimated to yield tMax = ln(ka/ke) / (ka - ke)
 */
function getRateConstants(halfLifeDays, tMaxDays) {
  const ke = Math.LN2 / Math.max(0.5, halfLifeDays);
  let ka = Math.max(ke * 2.5, 2.0 / Math.max(0.5, tMaxDays));
  
  // Newton refinement for ka
  for (let i = 0; i < 5; i++) {
    const f = (Math.log(ka / ke) / (ka - ke)) - tMaxDays;
    const df = ((1 / ka) * (ka - ke) - Math.log(ka / ke)) / Math.pow(ka - ke, 2);
    if (Math.abs(df) > 1e-6) {
      ka = Math.max(ke * 1.5, ka - f / df);
    }
  }

  // Peak normalization factor so single dose D reaches maximum = D
  const tPeak = Math.log(ka / ke) / (ka - ke);
  const normFactor = (ka / (ka - ke)) * (Math.exp(-ke * tPeak) - Math.exp(-ka * tPeak));

  return { ke, ka, normFactor: normFactor > 0 ? normFactor : 1 };
}

/**
 * Single dose contribution at delta days post-injection.
 */
function calculateDoseLevel(doseMg, deltaDays, { ke, ka, normFactor }) {
  if (deltaDays < 0) return 0;
  const raw = (ka / (ka - ke)) * (Math.exp(-ke * deltaDays) - Math.exp(-ka * deltaDays));
  return (doseMg / normFactor) * Math.max(0, raw);
}

/**
 * Reconstructs protocol historical intervals from audit logs.
 * Identifies earliest history date and any historical changes to dosage, schedule, or status.
 */
export function reconstructProtocolHistory(protocol, allAuditLogs = []) {
  const todayStr = formatDateIso(new Date());
  const protocolId = protocol.id;
  
  // Find all audit logs belonging to this protocol
  const logs = (allAuditLogs || []).filter(l => l.protocolId === protocolId);
  // Sort chronologically ascending
  logs.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  // Determine earliest recorded date in history
  let earliestDateStr = protocol.startDate || todayStr;
  
  // Check protocol createdAt
  if (protocol.createdAt) {
    try {
      const createdStr = formatDateIso(new Date(protocol.createdAt));
      if (createdStr && createdStr < earliestDateStr) {
        earliestDateStr = createdStr;
      }
    } catch {
      // ignore
    }
  }

  // Check audit logs for earlier creation or earlier startDate in changedFields
  logs.forEach(log => {
    if (log.timestamp) {
      try {
        const logDateStr = formatDateIso(new Date(log.timestamp));
        if (logDateStr && logDateStr < earliestDateStr) {
          earliestDateStr = logDateStr;
        }
      } catch {
        // ignore
      }
    }
    if (log.changedFields?.startDate?.old) {
      if (log.changedFields.startDate.old < earliestDateStr) {
        earliestDateStr = log.changedFields.startDate.old;
      }
    }
    if (log.changedFields?.startDate?.new) {
      if (log.changedFields.startDate.new < earliestDateStr) {
        earliestDateStr = log.changedFields.startDate.new;
      }
    }
  });

  // Reconstruct dosage timeline from update logs.
  // By walking backwards from the current protocol state, dosage and unit transitions
  // remain correctly synchronized even when audit logs change unit and dosage separately or together.
  const currentDoseMg = normalizeDoseToMg(protocol);
  const dosageTransitions = [];

  let curDosage = protocol.dosageMin;
  let curUnit = protocol.unit || 'mg';

  for (let i = logs.length - 1; i >= 0; i--) {
    const log = logs[i];
    const changed = log.changedFields;
    const hasDoseChange = changed && (changed.dosageMin !== undefined || changed.unit !== undefined);
    if (!hasDoseChange) continue;

    const ts = log.timestamp ? formatDateIso(new Date(log.timestamp)) : todayStr;
    const newDoseMg = normalizeDoseValueToMg(curDosage, curUnit);

    // Revert state to before this log
    if (changed.dosageMin?.old !== undefined && changed.dosageMin.old !== null) {
      curDosage = changed.dosageMin.old;
    }
    if (changed.unit?.old !== undefined && changed.unit.old !== null) {
      curUnit = changed.unit.old;
    }

    const oldDoseMg = normalizeDoseValueToMg(curDosage, curUnit);

    dosageTransitions.unshift({
      dateStr: ts,
      oldDose: oldDoseMg > 0 ? oldDoseMg : currentDoseMg,
      newDose: newDoseMg > 0 ? newDoseMg : currentDoseMg
    });
  }

  const getDoseAtDate = (dateStr) => {
    if (dosageTransitions.length === 0) return currentDoseMg;
    // Find the latest transition that happened on or before dateStr
    for (let i = dosageTransitions.length - 1; i >= 0; i--) {
      const t = dosageTransitions[i];
      if (dateStr >= t.dateStr) {
        return t.newDose;
      }
    }
    // If date is before the first transition, use the first old dose
    return dosageTransitions[0].oldDose || currentDoseMg;
  };

  return {
    earliestDateStr,
    getDoseAtDate,
    hasAuditHistory: logs.length > 0
  };
}

/**
 * Reconstructs all dose events (past, current, future) for a matched compound
 * by examining all protocols (Active, Completed, Paused) and audit log history.
 */
export function buildCompoundHistoricalDoses({
  compound,
  protocols = [],
  auditLogs = [],
  horizonStart,
  horizonEnd,
  extrapolateOngoingBackwards = true
}) {
  const doses = [];
  const todayStr = formatDateIso(new Date());

  // Gather all protocols matching this compound
  const compoundProtocols = protocols.filter(p => {
    const comp = identifyCompound(p);
    return comp && comp.id === compound.id;
  });

  if (compoundProtocols.length === 0) {
    return doses;
  }

  // Find all audit logs relevant to this compound
  const protocolIds = new Set(compoundProtocols.map(p => p.id));
  const compoundLogs = (auditLogs || []).filter(l => {
    if (protocolIds.has(l.protocolId)) return true;
    const notes = (l.notes || '').toLowerCase();
    return compound.aliases.some(alias => notes.includes(alias.toLowerCase()));
  });

  // Track doses on specific dates to prevent double-counting
  const scheduledDates = new Map(); // dateStr -> doseMg

  // Find the latest end date among completed cycles for this compound.
  // Backward extrapolation of a newly started active protocol must never reach
  // past this boundary, otherwise the synthetic steady-state curve for the new
  // dose would overwrite the real historical curve of the cycle it replaced.
  let latestCompletedEndStr = null;
  compoundProtocols.forEach(protocol => {
    const effectiveStatus = calculateEffectiveStatus(protocol, todayStr);
    if (effectiveStatus !== 'Completed') return;
    if (!protocol.endDate) return;
    if (!latestCompletedEndStr || protocol.endDate > latestCompletedEndStr) {
      latestCompletedEndStr = protocol.endDate;
    }
  });

  // Resolve the effective [start, end] interval of every protocol up front.
  // The protocol's own start date is authoritative; audit history is only a
  // fallback when no start date is set (it must never push the start earlier
  // than what the user explicitly entered).
  const resolved = compoundProtocols.map(protocol => {
    // Derive status from dates as well as the stored flag, so a protocol whose
    // endDate has passed is treated as ended even if cycleStatus was never updated.
    const effectiveStatus = calculateEffectiveStatus(protocol, todayStr);
    const isCompleted = effectiveStatus === 'Completed';
    const isPaused = effectiveStatus === 'Paused';
    const isActive = effectiveStatus === 'Active';

    // Reconstruct audit history for this protocol
    const history = reconstructProtocolHistory(protocol, compoundLogs);

    let startStr = protocol.startDate || history.earliestDateStr;

    // If protocol is active and started recently (e.g. today or last few days),
    // and extrapolateOngoingBackwards is enabled, extrapolate backward across horizon
    // so user sees their ongoing schedule accumulation leading up to today.
    // Only genuinely active protocols may be extrapolated; ended/paused ones keep
    // their real historical window.
    //
    // The backward window is capped at the day after the latest completed cycle
    // for this compound. Without this cap a newly started protocol would be
    // extrapolated over the whole chart, its interval would overlap (and thus
    // suppress) the completed protocol, and only the new dose's synthetic
    // steady-state curve would remain visible.
    const daysSinceStart = (parseDateIso(todayStr).getTime() - parseDateIso(startStr).getTime()) / 86400000;
    if (isActive && daysSinceStart < 14 && extrapolateOngoingBackwards) {
      let earlyStr = formatDateIso(parseDateIso(horizonStart));
      if (latestCompletedEndStr) {
        const dayAfterCompleted = parseDateIso(latestCompletedEndStr);
        dayAfterCompleted.setDate(dayAfterCompleted.getDate() + 1);
        const dayAfterCompletedStr = formatDateIso(dayAfterCompleted);
        if (dayAfterCompletedStr > earlyStr) {
          earlyStr = dayAfterCompletedStr;
        }
      }
      if (earlyStr < startStr) {
        startStr = earlyStr;
      }
    }

    // Resolve the end boundary. An explicit endDate always caps the schedule,
    // ended/paused protocols never project doses into the future, and nothing
    // may extend past the timeline horizon.
    let endStr = horizonEnd;
    if (protocol.endDate && protocol.endDate < endStr) {
      endStr = protocol.endDate;
    }
    if (isCompleted) {
      endStr = protocol.endDate || todayStr;
      if (todayStr < endStr) endStr = todayStr;
    } else if (isPaused) {
      if (todayStr < endStr) endStr = todayStr;
    }
    if (horizonEnd < endStr) {
      endStr = horizonEnd;
    }

    return { protocol, history, effectiveStatus, isCompleted, isPaused, isActive, startStr, endStr };
  });

  // Active / paused / pending protocols always win. A completed protocol is only
  // considered if its interval does not genuinely overlap a non-completed one
  // (boundary-touching intervals, e.g. an old cycle ending the day a new one
  // starts, are kept so their historical doses are still counted).
  const liveIntervals = resolved.filter(r => !r.isCompleted);
  const effective = resolved.filter(r => {
    if (!r.isCompleted) return true;
    return !liveIntervals.some(l => r.startStr <= l.endStr && r.endStr > l.startStr);
  });

  effective.forEach(({ protocol, history, startStr, endStr }) => {

    // Iterate day by day across [startStr, endStr]
    const cur = parseDateIso(startStr);
    const end = parseDateIso(endStr);
    let safety = 0;

    while (cur <= end && safety < 1500) {
      safety++;
      const curStr = formatDateIso(cur);

      // Check if scheduled on this calendar day
      // Create a temporary protocol model with the start boundary relaxed for schedule testing
      const testProtocol = {
        ...protocol,
        startDate: startStr
      };

      if (isProtocolScheduledOnDate(testProtocol, cur)) {
        // Resolve historical or projected dose for this date
        const doseMg = history.getDoseAtDate(curStr);
        if (doseMg > 0) {
          // If already scheduled on this day, use maximum
          const existing = scheduledDates.get(curStr);
          if (!existing || doseMg > existing) {
            scheduledDates.set(curStr, doseMg);
          }
        }
      }

      cur.setDate(cur.getDate() + 1);
    }
  });

  // Convert map to sorted dose events array
  scheduledDates.forEach((doseMg, dateStr) => {
    const d = parseDateIso(dateStr);
    doses.push({
      dateStr,
      timestamp: d.getTime(),
      doseMg,
      isFuture: dateStr > todayStr
    });
  });

  doses.sort((a, b) => a.timestamp - b.timestamp);
  return doses;
}

/**
 * Generate serum concentration timeline across a date range.
 * Integrates protocol history, audit logs, and backwards extrapolation.
 */
export function generateSerumTimeline({
  protocols = [],
  auditLogs = [],
  timeRange = '3months', // '2weeks' | '1month' | '3months' | 'all'
  referenceDate = new Date(),
  extrapolateOngoingBackwards = true
}) {
  const todayStr = formatDateIso(referenceDate);
  const todayTime = parseDateIso(todayStr).getTime();

  // 1. Identify all eligible GLP-1 / amylin protocols (Active, Completed, Paused)
  const matchedProtocols = [];
  protocols.forEach(p => {
    const compound = identifyCompound(p);
    if (compound) {
      matchedProtocols.push({
        protocol: p,
        compound
      });
    }
  });

  if (matchedProtocols.length === 0) {
    return {
      hasProtocols: false,
      compounds: [],
      dataPoints: [],
      currentLevels: {},
      totalCurrentLevel: 0,
      steadyStateInfo: {},
      sevenDayAverages: {}
    };
  }

  // 2. Determine date horizon based on timeRange
  // Calculate earliest date across all protocols and audit logs
  let earliestProtocolTime = todayTime;
  matchedProtocols.forEach(({ protocol }) => {
    if (protocol.startDate) {
      const t = parseDateIso(protocol.startDate).getTime();
      if (t < earliestProtocolTime) earliestProtocolTime = t;
    }
    if (protocol.createdAt) {
      const t = new Date(protocol.createdAt).getTime();
      if (!isNaN(t) && t < earliestProtocolTime) earliestProtocolTime = t;
    }
  });
  (auditLogs || []).forEach(log => {
    if (log.timestamp) {
      const t = new Date(log.timestamp).getTime();
      if (!isNaN(t) && t < earliestProtocolTime) earliestProtocolTime = t;
    }
    if (log.changedFields?.startDate?.old) {
      const t = parseDateIso(log.changedFields.startDate.old).getTime();
      if (!isNaN(t) && t < earliestProtocolTime) earliestProtocolTime = t;
    }
    if (log.changedFields?.startDate?.new) {
      const t = parseDateIso(log.changedFields.startDate.new).getTime();
      if (!isNaN(t) && t < earliestProtocolTime) earliestProtocolTime = t;
    }
  });

  const msSinceEarliest = Math.max(0, todayTime - earliestProtocolTime);
  const totalDaysSinceEarliest = Math.ceil(msSinceEarliest / 86400000);

  let daysPast = 90;
  let daysFuture = 28;

  if (timeRange === '2weeks') {
    daysPast = 14;
    daysFuture = 7;
  } else if (timeRange === '1month') {
    daysPast = 30;
    daysFuture = 14;
  } else if (timeRange === '3months') {
    daysPast = 90;
    daysFuture = 28;
  } else if (timeRange === '6months') {
    daysPast = 180;
    daysFuture = 28;
  } else if (timeRange === 'all') {
    // Span the entire historical span back to the earliest dose/start date plus a 14-day pre-buffer
    daysPast = Math.max(30, totalDaysSinceEarliest + 14);
    daysFuture = 28;
  }

  const startDate = new Date(referenceDate);
  startDate.setDate(startDate.getDate() - daysPast);
  const startStr = formatDateIso(startDate);

  const endDate = new Date(referenceDate);
  endDate.setDate(endDate.getDate() + daysFuture);
  const endStr = formatDateIso(endDate);

  // Pre-horizon buffer for pharmacokinetic wash-in accumulation
  // Doses administered up to 35 days before startStr still have residual serum concentration
  const bufferStartDate = new Date(startDate);
  bufferStartDate.setDate(bufferStartDate.getDate() - 35);
  const bufferStartStr = formatDateIso(bufferStartDate);

  // 3. For each unique compound, gather all doses and calculate curves
  const uniqueCompoundIds = Array.from(new Set(matchedProtocols.map(m => m.compound.id)));
  const uniqueCompounds = uniqueCompoundIds.map(id => TRACKED_COMPOUNDS[id]);

  // Pre-calculate rate constants per compound
  const constantsByCompound = {};
  uniqueCompounds.forEach(compound => {
    constantsByCompound[compound.id] = getRateConstants(compound.halfLifeDays, compound.tMaxDays);
  });

  // Build unified historical doses per compound
  const compoundDoses = {};
  uniqueCompounds.forEach(compound => {
    compoundDoses[compound.id] = buildCompoundHistoricalDoses({
      compound,
      protocols,
      auditLogs,
      horizonStart: bufferStartStr,
      horizonEnd: endStr,
      extrapolateOngoingBackwards
    });
  });

  // 4. Generate daily discrete data points across [startStr, endStr]
  const dataPoints = [];
  const cur = parseDateIso(startStr);
  const end = parseDateIso(endStr);

  while (cur <= end) {
    const curStr = formatDateIso(cur);
    const curTime = cur.getTime();
    const isToday = curStr === todayStr;
    const isFuture = curTime > todayTime;

    const point = {
      dateStr: curStr,
      timestamp: curTime,
      isToday,
      isFuture,
      displayDate: cur.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      totalLevel: 0
    };

    // Calculate level for each compound
    uniqueCompoundIds.forEach(cId => {
      const constants = constantsByCompound[cId];
      const doses = compoundDoses[cId] || [];
      let level = 0;

      doses.forEach(dose => {
        const deltaDays = (curTime - dose.timestamp) / 86400000;
        if (deltaDays >= 0) {
          level += calculateDoseLevel(dose.doseMg, deltaDays, constants);
        }
      });

      point[cId] = Math.round(level * 100) / 100;
      point.totalLevel += point[cId];
    });

    point.totalLevel = Math.round(point.totalLevel * 100) / 100;
    dataPoints.push(point);

    cur.setDate(cur.getDate() + 1);
  }

  // 5. Current level & 7-day average metrics
  const todayPoint = dataPoints.find(p => p.isToday) || dataPoints[dataPoints.length - 1] || {};
  const currentLevels = {};
  let totalCurrentLevel = 0;

  uniqueCompoundIds.forEach(cId => {
    currentLevels[cId] = todayPoint[cId] || 0;
    totalCurrentLevel += currentLevels[cId];
  });
  totalCurrentLevel = Math.round(totalCurrentLevel * 100) / 100;

  // 7-day rolling average around today (past 7 days including today)
  const todayIdx = dataPoints.findIndex(p => p.isToday);
  const sevenDayAverages = {};
  if (todayIdx >= 0) {
    const startIdx = Math.max(0, todayIdx - 6);
    const windowPoints = dataPoints.slice(startIdx, todayIdx + 1);
    const count = windowPoints.length || 1;

    uniqueCompoundIds.forEach(cId => {
      const sum = windowPoints.reduce((acc, p) => acc + (p[cId] || 0), 0);
      sevenDayAverages[cId] = Math.round((sum / count) * 100) / 100;
    });

    const totalSum = windowPoints.reduce((acc, p) => acc + (p.totalLevel || 0), 0);
    sevenDayAverages.total = Math.round((totalSum / count) * 100) / 100;
  } else {
    uniqueCompoundIds.forEach(cId => {
      sevenDayAverages[cId] = currentLevels[cId] || 0;
    });
    sevenDayAverages.total = totalCurrentLevel;
  }

  // 6. Steady state calculation broken out by compound
  const steadyStateInfo = {};
  uniqueCompounds.forEach(compound => {
    // Prefer the most recently started protocol that is still effectively active.
    // Fall back to the most recently started protocol for this compound, so an
    // ended cycle never overrides the newer one that replaced it.
    const candidates = matchedProtocols
      .filter(m => m.compound.id === compound.id)
      .map(m => ({ ...m, effectiveStatus: calculateEffectiveStatus(m.protocol, todayStr) }))
      .sort((a, b) => String(b.protocol.startDate || '').localeCompare(String(a.protocol.startDate || '')));

    const protoObj = candidates.find(m => m.effectiveStatus === 'Active') || candidates[0];

    const protocol = protoObj?.protocol || {};
    const effectiveStatus = protoObj?.effectiveStatus || calculateEffectiveStatus(protocol, todayStr);
    const history = reconstructProtocolHistory(protocol, auditLogs);
    const startT = parseDateIso(history.earliestDateStr || protocol.startDate || todayStr).getTime();
    
    // If protocol was extrapolated backwards, active days spans the backwards window
    let daysActive = Math.max(0, (todayTime - startT) / 86400000);
    if (daysActive < 14 && extrapolateOngoingBackwards) {
      daysActive = 42; // standard multi-week ongoing steady state
    }

    const ke = constantsByCompound[compound.id].ke;
    const saturationFraction = 1 - Math.exp(-ke * daysActive);
    const saturationPct = Math.min(100, Math.max(1, Math.round(saturationFraction * 100)));

    const doseMg = normalizeDoseToMg(protocol) || 1;
    const tau = 7; // weekly dosing standard
    const expTerm = Math.exp(-ke * tau);
    const ssPeak = Math.round((doseMg / (1 - expTerm)) * 100) / 100;
    const ssTrough = Math.round((ssPeak * expTerm) * 100) / 100;
    const ssAverage = Math.round((doseMg / (ke * tau)) * 100) / 100;

    steadyStateInfo[compound.id] = {
      compoundName: compound.name,
      daysActive: Math.round(daysActive),
      saturationPct,
      isNearSteadyState: saturationPct >= 90,
      theoreticalPeak: ssPeak,
      theoreticalTrough: ssTrough,
      theoreticalAverage: ssAverage,
      protocolName: protocol.name,
      protocolId: protocol.id,
      cycleStatus: effectiveStatus,
      doseMg,
      frequencySchedule: protocol.frequencySchedule || 'Weekly',
      hasAuditHistory: history.hasAuditHistory,
      earliestDateStr: history.earliestDateStr
    };
  });

  return {
    hasProtocols: true,
    compounds: uniqueCompounds,
    dataPoints,
    currentLevels,
    totalCurrentLevel,
    steadyStateInfo,
    sevenDayAverages,
    todayPoint,
    startDateStr: startStr,
    endDateStr: endStr
  };
}

/**
 * Generate smooth SVG path data using Fritsch-Carlson monotonic cubic splines.
 * Eliminates artificial dips/undershoots below the baseline (e.g. at the start of a protocol)
 * while preserving smooth, continuous peaks and decay slopes.
 */
export function buildSplineSvgPath(points, getX, getY) {
  const n = points ? points.length : 0;
  if (n === 0) return '';
  if (n === 1) return `M ${getX(points[0]).toFixed(1)} ${getY(points[0]).toFixed(1)}`;

  const xs = new Float64Array(n);
  const ys = new Float64Array(n);

  for (let i = 0; i < n; i++) {
    xs[i] = getX(points[i]);
    ys[i] = getY(points[i]);
  }

  // 1. Calculate secant slopes: delta_k = (ys[k+1] - ys[k]) / (xs[k+1] - xs[k])
  const deltas = new Float64Array(n - 1);
  const m = new Float64Array(n);

  for (let i = 0; i < n - 1; i++) {
    const dx = xs[i + 1] - xs[i];
    deltas[i] = dx !== 0 ? (ys[i + 1] - ys[i]) / dx : 0;
  }

  // 2. Initialize tangents as averages of adjacent secants
  m[0] = deltas[0];
  m[n - 1] = deltas[n - 2];

  for (let i = 1; i < n - 1; i++) {
    // If adjacent slopes have opposite signs or either is flat, tangent must be 0
    if (deltas[i - 1] * deltas[i] <= 0) {
      m[i] = 0;
    } else {
      m[i] = (deltas[i - 1] + deltas[i]) / 2;
    }
  }

  // 3. Fritsch-Carlson conditions to guarantee strict monotonicity
  for (let i = 0; i < n - 1; i++) {
    if (deltas[i] === 0) {
      m[i] = 0;
      m[i + 1] = 0;
    } else {
      const alpha = m[i] / deltas[i];
      const beta = m[i + 1] / deltas[i];
      const dist = alpha * alpha + beta * beta;
      if (dist > 9) {
        const tau = 3 / Math.sqrt(dist);
        m[i] = tau * alpha * deltas[i];
        m[i + 1] = tau * beta * deltas[i];
      }
    }
  }

  // Baseline limit is getY(0) (in SVG, higher Y value = lower on screen)
  const baselineY = getY(0);
  let path = `M ${xs[0].toFixed(1)} ${ys[0].toFixed(1)}`;

  for (let i = 0; i < n - 1; i++) {
    const x0 = xs[i];
    const y0 = ys[i];
    const x1 = xs[i + 1];
    const y1 = ys[i + 1];
    const dx = (x1 - x0) / 3;

    let cp1x = x0 + dx;
    let cp1y = y0 + m[i] * dx;
    let cp2x = x1 - dx;
    let cp2y = y1 - m[i + 1] * dx;

    // Strict non-negative clamp: curves must never dip below baseline Y
    cp1y = Math.min(baselineY, cp1y);
    cp2y = Math.min(baselineY, cp2y);

    // Bounded monotonicity clamp between adjacent points
    const minY = Math.min(y0, y1);
    const maxY = Math.max(y0, y1);
    cp1y = Math.max(minY, Math.min(maxY, cp1y));
    cp2y = Math.max(minY, Math.min(maxY, cp2y));

    path += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${x1.toFixed(1)} ${y1.toFixed(1)}`;
  }

  return path;
}
